import { setIsolatedAuthToken } from './authToken';

type HandoffMessage =
  | { type: 'READY' }
  | { type: 'TOKEN'; token: string }
  | { type: 'STORED' };

const CHANNEL_QUERY_PARAM = 'authChannel';
const HANDOFF_TIMEOUT_MS = 30000;

export const openTabWithAuthToken = (url: string, token: string): void => {
  const channelId = crypto.randomUUID();
  const channel = new BroadcastChannel(`auth-handoff:${channelId}`);
  let tokenSent = false;

  const closeChannel = () => {
    window.clearTimeout(timeoutId);
    channel.close();
  };

  channel.onmessage = ({ data }: MessageEvent<HandoffMessage>) => {
    if (data?.type === 'READY' && !tokenSent) {
      tokenSent = true;
      channel.postMessage({ type: 'TOKEN', token } satisfies HandoffMessage);
    } else if (data?.type === 'STORED') {
      closeChannel();
    }
  };

  const timeoutId = window.setTimeout(() => {
    closeChannel();
    console.error(
      'Timed out transferring the authentication token to the new tab.'
    );
  }, HANDOFF_TIMEOUT_MS);

  const targetUrl = new URL(url, window.location.origin);
  targetUrl.searchParams.set(CHANNEL_QUERY_PARAM, channelId);
  window.open(targetUrl.toString(), '_blank', 'noopener,noreferrer');
};

export const receiveAuthTokenHandoff = (): Promise<void> | null => {
  const currentUrl = new URL(window.location.href);
  const channelId = currentUrl.searchParams.get(CHANNEL_QUERY_PARAM);
  if (!channelId) return null;

  currentUrl.searchParams.delete(CHANNEL_QUERY_PARAM);
  window.history.replaceState(
    window.history.state,
    '',
    `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
  );

  return new Promise((resolve, reject) => {
    const channel = new BroadcastChannel(`auth-handoff:${channelId}`);
    const timeoutId = window.setTimeout(() => {
      channel.close();
      reject(new Error('Timed out waiting for the authentication token.'));
    }, HANDOFF_TIMEOUT_MS);

    channel.onmessage = ({ data }: MessageEvent<HandoffMessage>) => {
      if (data?.type !== 'TOKEN' || !data.token) return;

      setIsolatedAuthToken(data.token);
      channel.postMessage({ type: 'STORED' } satisfies HandoffMessage);
      window.clearTimeout(timeoutId);
      channel.close();
      resolve();
    };

    channel.postMessage({ type: 'READY' } satisfies HandoffMessage);
  });
};
